import { Controller } from '@hotwired/stimulus';
import { put } from '@rails/request.js';
import { loadStripe } from '@stripe/stripe-js';
import Mustache from 'mustache';

export class StripeExpressCheckoutController extends Controller {
  static targets = [
    'addressAddress1',
    'addressAddress2',
    'addressCity',
    'addressCountry',
    'addressName',
    'addressPhone',
    'addressState',
    'addressZipcode',
    'email',
    'error',
    'errorTemplate',
    'paymentMethod',
    'paymentMethods',
    'paymentMethodToken',
    'shippingOption',
  ];

  static values = {
    amount: Number,
    businessName: String,
    items: Array,
    publicKey: String,
    shippingOptions: Array,
    shippingAddressUrl: String,
    shippingOptionUrl: String,
  };

  async connect() {
    this.stripe = await loadStripe(this.publicKeyValue);

    const options = {
      buttonTheme: {
        applePay: 'black',
        googlePay: 'black',
        paypal: 'black',
      },
      buttonType: {
        googlePay: 'plain',
      },
      paymentMethods: {
        applePay: 'always',
        googlePay: 'always',
        link: 'never',
      },
    };

    this.elements = this.stripe.elements({
      mode: 'payment',
      amount: this.amountValue,
      currency: 'usd',
      paymentMethodCreation: 'manual',
      appearance: {},
    });

    this.expressCheckoutElement = this.elements.create('expressCheckout', options);

    this.expressCheckoutElement.on('ready', ({ availablePaymentMethods }) => {
      if (!availablePaymentMethods) return;

      this.element.classList.remove('hidden');
    });

    this.mountElement();
  }

  mountElement() {
    this.expressCheckoutElement.mount(this.paymentMethodsTarget);

    this.expressCheckoutElement.on('click', event => {
      const options = {
        business: {
          name: this.businessNameValue,
        },
        emailRequired: true,
        phoneNumberRequired: true,
        shippingAddressRequired: true,
        lineItems: this.itemsValue,
      };

      options.shippingRates = this.shippingOptionsValue.map(option => ({
        ...option,
        displayName: option.label,
      }));

      event.resolve(options);
    });

    this.expressCheckoutElement.on('shippingaddresschange', async event => {
      const params = {
        shipping_address: {
          city: event.address.city,
          state: event.address.state,
          zipcode: event.address.postal_code,
          country: event.address.country,
        },
        validation_kind: 'basic',
      };

      const response = await put(this.shippingAddressUrlValue, {
        body: JSON.stringify(params),
        contentType: 'application/json',
        responseKind: 'json',
      });

      if (!response.ok) {
        event.reject();
        return;
      }

      const body = await response.json;

      if (!body.success) {
        event.reject();
        return;
      }

      this.elements.update({ amount: body.amount });

      event.resolve({
        lineItems: body.items,
        shippingRates: body.shipping_options.map(option => ({
          ...option,
          displayName: option.label,
        })),
      });
    });

    this.expressCheckoutElement.on('shippingratechange', async event => {
      const params = {
        order: {
          shipping_option: event.shippingRate.id,
        },
      };

      const response = await put(this.shippingOptionUrlValue, {
        body: JSON.stringify(params),
        contentType: 'application/json',
        responseKind: 'json',
      });

      if (!response.ok) {
        event.reject();
        return;
      }

      const body = await response.json;

      if (!body.success) {
        event.reject();
        return;
      }

      this.elements.update({ amount: body.amount });

      event.resolve({
        lineItems: body.items,
        shippingRates: body.shipping_options.map(option => ({
          ...option,
          displayName: option.label,
        })),
      });
    });

    this.expressCheckoutElement.on('confirm', async event => {
      const { error: submitError } = await this.elements.submit();
      if (submitError) {
        this.setFormError(error);
        return;
      }

      const { error, paymentMethod } = await this.stripe.createPaymentMethod({
        elements: this.elements,
      });

      if (error) {
        this.setFormError(error);
        return;
      }

      this.paymentMethodTokenTarget.value = paymentMethod.id;
      this.paymentMethodTarget.value = event.expressPaymentType;
      this.emailTarget.value = event.billingDetails.email;
      this.addressNameTarget.value = event.shippingAddress.name;
      this.addressAddress1Target.value = event.shippingAddress.address.line1;
      this.addressAddress2Target.value = event.shippingAddress.address.line2;
      this.addressCityTarget.value = event.shippingAddress.address.city;
      this.addressStateTarget.value = event.shippingAddress.address.state;
      this.addressZipcodeTarget.value = event.shippingAddress.address.postal_code;
      this.addressCountryTarget.value = event.shippingAddress.address.country;
      this.shippingOptionTarget.value = event.shippingRate.id;

      if (event.billingDetails.phone) {
        this.addressPhoneTarget.value = event.billingDetails.phone;
      }

      this.element.requestSubmit();
    });

    this.expressCheckoutElement.on('cancel', () => {
      this.elements.update({ amount: this.amountValue });
    });
  };

  // TODO: make reusable
  setFormError(error) {
    if (this.hasErrorTarget) this.errorTarget.remove();

    this.errorTemplateTarget.parentNode.insertAdjacentHTML(
      'afterbegin',
      Mustache.render(this.errorTemplateTarget.innerHTML, {
        error: error.message || 'Your payment failed. Please try again.',
      }),
    );
  };
}
